import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import PhoneIcon from "@atlaskit/icon/core/phone";
import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import {token} from "@atlaskit/tokens";
import {useState, type ReactNode} from "react";
import {BottomDrawer} from "~/components/BottomDrawer";
import {AnankeTempDropdownMenuWithIconButtonTrigger} from "~/components/anankeTemp/anankeTempDropdownMenuWithIconButtonTrigger";
import {WhatsappIcon} from "~/components/svgs";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {AnankeText} from "@vesta/ananke/components/anankeText";

const MOBILE_TOOLBAR_ICON_BUTTON_CLASS =
    "flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[3px] border";

const MOBILE_TOOLBAR_ICON_BUTTON_STYLE = {
    borderColor: token("color.border", "#091E4224"),
    backgroundColor: token("elevation.surface", "#FFFFFF"),
    color: token("color.icon", "#44546F"),
} as const;

const MOBILE_DRAWER_BOTTOM_SHEET_PROPS = {
    zIndex: 100,
    fitContentOnOpen: false as const,
    initialOpenHeight: 480,
};

function MobileOptionRowButton({
    label,
    onClick,
    icon,
}: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: token("space.150", "12px"),
                height: "40px",
                minHeight: "32px",
                padding: `0 ${token("space.150", "12px")}`,
                borderRadius: "4px",
                background: token("color.background.input"),
                color: token("color.text"),
                cursor: "pointer",
            }}
        >
            <div style={{display: "flex", alignItems: "center", gap: token("space.150", "12px")}}>
                {icon}
                <AnankeText color="color.text.subtle">{label}</AnankeText>
            </div>
            <ChevronRightIcon label="" color={token("color.icon.subtle")} />
        </button>
    );
}

function JourneyContactDropdownItems({
    callNumber,
    whatsAppNumberCleaned,
}: {
    callNumber: string | null;
    whatsAppNumberCleaned: string | null;
}) {
    const hasCallNumber = callNumber !== null && callNumber !== "";
    const hasWhatsAppNumber = whatsAppNumberCleaned !== null && whatsAppNumberCleaned !== "";

    return (
        <AnankeDropdownItemGroup>
            {hasWhatsAppNumber === true ? (
                <AnankeDropdownItem
                    href={`https://wa.me/${whatsAppNumberCleaned}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div className="grid grid-flow-col gap-2 items-center justify-start">
                        <WhatsappIcon />
                        <span>WhatsApp</span>
                    </div>
                </AnankeDropdownItem>
            ) : null}
            {hasCallNumber === true ? (
                <AnankeDropdownItem href={`tel:${callNumber}`} target="_blank" rel="noopener noreferrer">
                    <div className="grid grid-flow-col gap-2 items-center justify-start">
                        <PhoneIcon label="Call" />
                        <span>Call</span>
                    </div>
                </AnankeDropdownItem>
            ) : null}
        </AnankeDropdownItemGroup>
    );
}

export function JourneyContactButton({
    callNumber,
    whatsAppNumberCleaned,
}: {
    callNumber: string | null;
    whatsAppNumberCleaned: string | null;
}) {
    const [opened, setOpened] = useState(false);

    const hasCallNumber = callNumber !== null && callNumber !== "";
    const hasWhatsAppNumber = whatsAppNumberCleaned !== null && whatsAppNumberCleaned !== "";

    if (hasCallNumber === false && hasWhatsAppNumber === false) {
        return null;
    }

    const contactItems = (
        <JourneyContactDropdownItems callNumber={callNumber} whatsAppNumberCleaned={whatsAppNumberCleaned} />
    );

    return (
        <>
            <div className="hidden sm:block">
                <AnankeDropdownMenu trigger="Contact">{contactItems}</AnankeDropdownMenu>
            </div>
            <div className="block sm:hidden">
                <button
                    type="button"
                    aria-label="Contact"
                    className={MOBILE_TOOLBAR_ICON_BUTTON_CLASS}
                    style={MOBILE_TOOLBAR_ICON_BUTTON_STYLE}
                    onClick={() => {
                        setOpened(true);
                    }}
                >
                    <PhoneIcon label="Contact" />
                </button>
                <BottomDrawer open={opened} setOpen={setOpened} title="Contact" {...MOBILE_DRAWER_BOTTOM_SHEET_PROPS}>
                    <div className="grid gap-ds-space-100" style={{fontFamily: "Inter"}}>
                        {hasWhatsAppNumber === true ? (
                            <MobileOptionRowButton
                                label="WhatsApp"
                                icon={<WhatsappIcon />}
                                onClick={() => {
                                    window.open(`https://wa.me/${whatsAppNumberCleaned}`, "_blank", "noopener,noreferrer");
                                    setOpened(false);
                                }}
                            />
                        ) : null}
                        {hasCallNumber === true ? (
                            <MobileOptionRowButton
                                label="Call"
                                icon={<PhoneIcon label="" />}
                                onClick={() => {
                                    window.location.href = `tel:${callNumber}`;
                                    setOpened(false);
                                }}
                            />
                        ) : null}
                    </div>
                </BottomDrawer>
            </div>
        </>
    );
}

function JourneyMoreActionsDropdownItems({
    showArchive,
    onArchive,
    onDelete,
}: {
    showArchive: boolean;
    onArchive: () => void;
    onDelete: () => void;
}) {
    return (
        <AnankeDropdownItemGroup>
            {showArchive === true ? (
                <AnankeDropdownItem onClick={onArchive}>Archive</AnankeDropdownItem>
            ) : (
                <AnankeDropdownItem onClick={onDelete}>Delete</AnankeDropdownItem>
            )}
        </AnankeDropdownItemGroup>
    );
}

export function JourneyMoreActionsButton({
    showArchive,
    onArchive,
    onDelete,
}: {
    showArchive: boolean;
    onArchive: () => void;
    onDelete: () => void;
}) {
    const [opened, setOpened] = useState(false);
    const actionLabel = showArchive === true ? "Archive" : "Delete";
    const actionItems = (
        <JourneyMoreActionsDropdownItems showArchive={showArchive} onArchive={onArchive} onDelete={onDelete} />
    );

    return (
        <>
            <div className="hidden sm:block">
                <AnankeTempDropdownMenuWithIconButtonTrigger
                    iconButtonProps={{
                        type: "button",
                        icon: ShowMoreHorizontalIcon,
                        label: "More actions",
                    }}
                >
                    {actionItems}
                </AnankeTempDropdownMenuWithIconButtonTrigger>
            </div>
            <div className="block sm:hidden">
                <button
                    type="button"
                    aria-label="More actions"
                    className={MOBILE_TOOLBAR_ICON_BUTTON_CLASS}
                    style={MOBILE_TOOLBAR_ICON_BUTTON_STYLE}
                    onClick={() => {
                        setOpened(true);
                    }}
                >
                    <ShowMoreHorizontalIcon label="More actions" />
                </button>
                <BottomDrawer open={opened} setOpen={setOpened} title="More actions" {...MOBILE_DRAWER_BOTTOM_SHEET_PROPS}>
                    <div className="grid gap-ds-space-100" style={{fontFamily: "Inter"}}>
                        <MobileOptionRowButton
                            label={actionLabel}
                            onClick={() => {
                                if (showArchive === true) {
                                    onArchive();
                                } else {
                                    onDelete();
                                }
                                setOpened(false);
                            }}
                        />
                    </div>
                </BottomDrawer>
            </div>
        </>
    );
}
